import {
  Box,
  Flex,
  Text,
  Menu,
  MenuButton,
  MenuList,
  MenuOptionGroup,
  MenuItemOption,
} from '@chakra-ui/react';
import { CloseIcon } from '@chakra-ui/icons';
import { ReactNode } from 'react';

import FigmaTriangleDown from 'components/Icons/FigmaTriangleDown';

import { accent, neutral, radius, shadow, text as textToken } from './designTokens';

/** Shared building blocks for the Dimensions side panels (Properties,
 *  Settings) so both read as one system instead of drifting apart. */

export const ROBOTO = "'Roboto Regular', Roboto, sans-serif";
export const ROBOTO_MEDIUM = "'Roboto Medium', 'Roboto Regular', Roboto, sans-serif";

export const fieldLabelStyle = {
  fontSize: '12px',
  fontWeight: 500,
  fontFamily: ROBOTO_MEDIUM,
  color: textToken.secondary,
  mb: '6px',
};

export const fieldInputStyle = {
  width: '100%',
  bg: neutral.surface,
  border: `1px solid ${neutral.border}`,
  borderRadius: radius.control,
  outline: 'none',
  color: textToken.primary,
  height: '32px',
  px: '11px',
  _placeholder: { color: textToken.muted },
  _hover: { borderColor: neutral.borderStrong },
  _focus: { borderColor: accent.border, boxShadow: 'none' },
  // Typography via sx so the Input theme's own variant defaults (which render
  // these in the browser fallback font at the wrong size) can't win.
  sx: { fontFamily: ROBOTO, fontSize: '13px' },
};

// Same divider-card recipe as the table's modern dropdowns / the dimension
// card's Rename menu, so every dropdown in the module reads as one system.
const menuOptionSx = {
  bg: neutral.surface,
  borderRadius: '0 !important',
  padding: '8px 11px',
  fontFamily: ROBOTO,
  fontSize: '13px',
  color: textToken.primary,
  _hover: { bg: neutral.surfaceHover },
  _focus: { bg: neutral.surface },
  '&[aria-checked="true"]': { bg: accent.subtle, color: textToken.primary },
  '.chakra-menu__icon-wrapper': { color: accent.solid },
};

interface SelectOption {
  value: string;
  label: string;
}

/** Replaces the native <Select> so the arrow, fonts and menu match the rest
 *  of the module instead of falling back to the browser's own control. */
export const PanelSelect = ({
  value,
  options,
  placeholder,
  onChange,
}: {
  value: string;
  options: SelectOption[];
  placeholder?: string;
  onChange: (value: string) => void;
}) => {
  const selected = options.find((option) => String(option.value) === String(value ?? ''));

  return (
    <Menu matchWidth gutter={4} autoSelect={false}>
      <MenuButton
        width='100%'
        bg='white'
        border='1px solid #D1D6DD'
        borderRadius={radius.control}
        height='36px'
        px='12px'
        textAlign='left'
        transition='border-color 0.15s ease'
        _hover={{ borderColor: neutral.borderStrong }}
        _expanded={{ borderColor: accent.border }}
      >
        <Flex alignItems='center' justifyContent='space-between' gap='8px'>
          <Text
            fontSize='13px'
            fontFamily={ROBOTO}
            color={selected?.label ? textToken.primary : textToken.muted}
            noOfLines={1}
          >
            {selected?.label || placeholder}
          </Text>
          <Box flexShrink={0} display='flex'>
            <FigmaTriangleDown width={8} height={5} color={textToken.muted} />
          </Box>
        </Flex>
      </MenuButton>
      <MenuList
        p={0}
        gap='1px'
        display='flex'
        flexDirection='column'
        bg={neutral.surface}
        border='none'
        borderRadius={radius.overlay}
        overflow='hidden'
        boxShadow={shadow.overlay}
        maxH='240px'
        overflowY='auto'
        className='scroll'
        zIndex={20}
      >
        <MenuOptionGroup
          type='radio'
          value={String(value ?? '')}
          onChange={(next: any) => onChange(String(next))}
          display='flex'
          flexDirection='column'
          gap='1px'
          m={0}
        >
          {options.map((option) => (
            <MenuItemOption key={option.value} value={String(option.value)} sx={menuOptionSx}>
              {option.label}
            </MenuItemOption>
          ))}
        </MenuOptionGroup>
      </MenuList>
    </Menu>
  );
};

/** Floating card + titled header shared by both side panels — matches the
 *  left Dimensions sidebar's card treatment. */
export const PanelShell = ({
  icon,
  title,
  onClose,
  children,
}: {
  icon: ReactNode;
  title: string;
  onClose: () => void;
  children: ReactNode;
}) => (
  <Flex
    flexDir='column'
    bg='white'
    borderRadius={radius.surface}
    border={`1px solid ${neutral.border}`}
    boxShadow={shadow.panel}
    width='310px'
    flexShrink={0}
    color={textToken.primary}
    height='100%'
    overflow='hidden'
  >
    <Flex
      alignItems='center'
      justifyContent='space-between'
      px='12px'
      pt='12px'
      pb='12px'
      borderBottom={`1px solid ${neutral.borderSubtle}`}
      flexShrink={0}
    >
      <Flex alignItems='center' gap='8px'>
        {icon}
        <Text fontSize='13px' fontWeight='600' fontFamily={ROBOTO_MEDIUM} color={textToken.primary}>
          {title}
        </Text>
      </Flex>
      <Box
        as='button'
        onClick={onClose}
        display='flex'
        alignItems='center'
        justifyContent='center'
        width='22px'
        height='22px'
        borderRadius={radius.control}
        transition='background 0.15s ease'
        _hover={{ bg: neutral.surfaceHover }}
      >
        <CloseIcon boxSize='9px' color={textToken.muted} />
      </Box>
    </Flex>
    <Flex flexDir='column' flex='1' minH={0}>
      {children}
    </Flex>
  </Flex>
);
